<template>
  <header class="header" :class="{ 'header-scrolled': isScrolled }">
    <div class="container">
      <div class="header-content">
        <!-- Logo -->
        <div class="logo animate__animated animate__fadeInLeft">
          <router-link to="/" @click.native="scrollToTop">
            <img class="logo-icon" src="@/assets/images/optimized/logo5.webp" alt="logo" />
          </router-link>
        </div>

        <!-- 桌面端导航 -->
        <nav class="desktop-nav animate__animated animate__fadeInRight">
          <!-- 关于华浩 -->
          <div class="nav-item-wrapper" @mouseenter="showDropdown('about')" @mouseleave="hideDropdown('about')">
            <a href="#about" class="nav-link" @click.prevent="scrollToAnchor('#about')">
              {{ $t("nav.about") }}
              <i class="arrow-down">▼</i>
            </a>
          </div>

          <!-- 服务范围 -->
          <div class="nav-item-wrapper" @mouseenter="showDropdown('services')" @mouseleave="hideDropdown('services')">
            <a href="#services" class="nav-link" @click.prevent="scrollToAnchor('#services')">
              {{ $t("nav.business") }}
              <i class="arrow-down">▼</i>
            </a>
          </div>

          <router-link to="/advantages" class="nav-link" @click.native="scrollToTop">
            {{ $t("nav.advantages") }}
          </router-link>

          <!-- 服务客户 -->
          <router-link to="/partners" class="nav-link" @click.native="scrollToTop">
            {{ $t("nav.partners") }}
          </router-link>

          <!-- 语言切换按钮 -->
          <div class="language-switcher">
            <el-button size="small" :type="currentLang === 'zh-HK' ? 'primary' : ''" @click="switchLanguage('zh-HK')" class="lang-btn"> 中文繁体 </el-button>
            <el-button size="small" :type="currentLang === 'en' ? 'primary' : ''" @click="switchLanguage('en')" class="lang-btn"> EN </el-button>
          </div>
        </nav>

        <!-- 移动端汉堡按钮 -->
        <button class="navbar-toggler" @click="toggleCollapse" :class="{ active: isCollapsed }" aria-label="Toggle navigation">
          <span class="navbar-toggler-icon"></span>
          <span class="navbar-toggler-icon"></span>
          <span class="navbar-toggler-icon"></span>
        </button>
      </div>

      <!-- 移动端折叠菜单 -->
      <transition name="collapse" @enter="onEnter" @after-enter="afterEnter" @leave="onLeave">
        <div v-show="isCollapsed" class="navbar-collapse">
          <ul class="navbar-nav">
            <li class="nav-item">
              <a href="#about" class="nav-link" @click.prevent="scrollToAnchor('#about', true)">
                {{ $t("nav.about") }}
              </a>
              <ul class="mobile-submenu">
                <li>
                  <router-link to="/company-intro" class="mobile-submenu-link" @click.native="scrollToTopAndClose">
                    {{ $t("nav.aboutSubmenu.companyIntro") }}
                  </router-link>
                </li>
                <li>
                  <router-link to="/contact" class="mobile-submenu-link" @click.native="scrollToTopAndClose">
                    {{ $t("nav.aboutSubmenu.contact") }}
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="nav-item">
              <a href="#services" class="nav-link" @click.prevent="scrollToAnchor('#services', true)">
                {{ $t("nav.business") }}
              </a>
              <ul class="mobile-submenu">
                <li class="mobile-submenu-title">{{ $t("nav.businessSubmenu.smartProducts.title") }}</li>
                <li>
                  <router-link to="/smart-wearable" class="mobile-submenu-link" @click.native="scrollToTopAndClose">
                    {{ $t("nav.businessSubmenu.smartProducts.items.smartWearable") }}
                  </router-link>
                </li>
                <li>
                  <router-link to="/personal-terminal" class="mobile-submenu-link" @click.native="scrollToTopAndClose">
                    {{ $t("nav.businessSubmenu.smartProducts.items.personalTerminal") }}
                  </router-link>
                </li>
                <li>
                  <router-link to="/home-terminal" class="mobile-submenu-link" @click.native="scrollToTopAndClose">
                    {{ $t("nav.businessSubmenu.smartProducts.items.homeTerminal") }}
                  </router-link>
                </li>
                <li>
                  <router-link to="/smart-robot" class="mobile-submenu-link" @click.native="scrollToTopAndClose">
                    {{ $t("nav.businessSubmenu.smartProducts.items.smartRobot") }}
                  </router-link>
                </li>
                <li class="mobile-submenu-title">{{ $t("nav.businessSubmenu.softwareProducts.title") }}</li>
                <li>
                  <router-link to="/ai-software" class="mobile-submenu-link" @click.native="scrollToTopAndClose">
                    {{ $t("nav.businessSubmenu.softwareProducts.items.aiSoftware") }}
                  </router-link>
                </li>
                <li>
                  <router-link to="/tech-service" class="mobile-submenu-link" @click.native="scrollToTopAndClose">
                    {{ $t("nav.businessSubmenu.softwareProducts.items.techService") }}
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="nav-item">
              <router-link to="/advantages" class="nav-link" @click.native="scrollToTopAndClose">
                {{ $t("nav.advantages") }}
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/partners" class="nav-link" @click.native="scrollToTopAndClose">
                {{ $t("nav.partners") }}
              </router-link>
            </li>
            <li class="nav-item language-mobile">
              <div class="language-switcher-mobile">
                <el-button size="small" :type="currentLang === 'zh-HK' ? 'primary' : ''" @click="switchLanguage('zh-HK')" class="lang-btn"> 中文繁体 </el-button>
                <el-button size="small" :type="currentLang === 'en' ? 'primary' : ''" @click="switchLanguage('en')" class="lang-btn"> EN </el-button>
              </div>
            </li>
          </ul>
        </div>
      </transition>
    </div>

    <!-- 下拉菜单 - 关于华浩 -->
    <transition name="dropdown">
      <div v-show="activeDropdown === 'about'" class="dropdown-menu" @mouseenter="showDropdown('about')" @mouseleave="hideDropdown('about')">
        <div class="dropdown-container">
          <div class="dropdown-content about-menu">
            <router-link to="/company-intro" class="menu-item" @click.native="scrollToTopAndClose">
              {{ $t("nav.aboutSubmenu.companyIntro") }}
            </router-link>
            <router-link to="/contact" class="menu-item" @click.native="scrollToTopAndClose">
              {{ $t("nav.aboutSubmenu.contact") }}
            </router-link>
          </div>
        </div>
      </div>
    </transition>

    <!-- 下拉菜单 - 服务范围 -->
    <transition name="dropdown">
      <div v-show="activeDropdown === 'services'" class="dropdown-menu" @mouseenter="showDropdown('services')" @mouseleave="hideDropdown('services')">
        <div class="dropdown-container">
          <div class="dropdown-content">
            <!-- 智能产品 -->
            <div class="menu-column">
              <div class="menu-title">{{ $t("nav.businessSubmenu.smartProducts.title") }}</div>
              <router-link to="/smart-wearable" class="menu-item" @click.native="scrollToTopAndClose">
                {{ $t("nav.businessSubmenu.smartProducts.items.smartWearable") }}
              </router-link>
              <router-link to="/personal-terminal" class="menu-item" @click.native="scrollToTopAndClose">
                {{ $t("nav.businessSubmenu.smartProducts.items.personalTerminal") }}
              </router-link>
              <router-link to="/home-terminal" class="menu-item" @click.native="scrollToTopAndClose">
                {{ $t("nav.businessSubmenu.smartProducts.items.homeTerminal") }}
              </router-link>
              <router-link to="/smart-robot" class="menu-item" @click.native="scrollToTopAndClose">
                {{ $t("nav.businessSubmenu.smartProducts.items.smartRobot") }}
              </router-link>
            </div>

            <!-- 软件产品 -->
            <div class="menu-column">
              <div class="menu-title">{{ $t("nav.businessSubmenu.softwareProducts.title") }}</div>
              <router-link to="/ai-software" class="menu-item" @click.native="scrollToTopAndClose">
                {{ $t("nav.businessSubmenu.softwareProducts.items.aiSoftware") }}
              </router-link>
              <router-link to="/tech-service" class="menu-item" @click.native="scrollToTopAndClose">
                {{ $t("nav.businessSubmenu.softwareProducts.items.techService") }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
export default {
  name: "AppHeader",
  data() {
    return {
      isScrolled: false,
      isCollapsed: false,
      activeSection: "home",
      currentLang: this.$i18n.locale,

      // 下拉菜单控制
      activeDropdown: null,
      dropdownTimer: null,
    };
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    this.updateActiveSection();
    // 初始化当前语言
    this.currentLang = this.$i18n.locale;
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
    if (this.dropdownTimer) {
      clearTimeout(this.dropdownTimer);
    }
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
      this.updateActiveSection();
    },

    updateActiveSection() {
      const sections = ["home", "business", "advantages", "about"];
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && scrollPosition >= section.offsetTop) {
          this.activeSection = sections[i];
          break;
        }
      }
    },

    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },

    closeCollapse() {
      this.isCollapsed = false;
    },

    closeDropdown() {
      this.activeDropdown = null;
      if (this.dropdownTimer) {
        clearTimeout(this.dropdownTimer);
      }
    },

    switchLanguage(lang) {
      this.$i18n.locale = lang;
      this.currentLang = lang;
      localStorage.setItem("language", lang);
      this.$message.success(lang === "zh-HK" ? "已切换至中文繁体" : "Switched to English");
    },

    // 滚动到页面顶部
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },

    // 滚动到锚点（先从顶部开始）
    scrollToAnchor(anchor, closeMenu = false) {
      // 先滚动到顶部
      window.scrollTo({
        top: 0,
        behavior: "instant", // 快速滚动到顶部
      });

      // 延迟一下，确保页面已经回到顶部
      setTimeout(() => {
        // 获取目标元素
        const element = document.querySelector(anchor);
        if (element) {
          // 计算元素位置
          const offset = 80; // 考虑header高度
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;

          // 滚动到目标位置
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 50);

      if (closeMenu) {
        this.closeCollapse();
      }
    },

    // 滚动到顶部并关闭菜单
    scrollToTopAndClose() {
      this.scrollToTop();
      this.closeCollapse();
      this.closeDropdown();
    },

    showDropdown(menu) {
      if (this.dropdownTimer) {
        clearTimeout(this.dropdownTimer);
        this.dropdownTimer = null;
      }
      this.activeDropdown = menu;
    },

    hideDropdown(menu) {
      this.dropdownTimer = setTimeout(() => {
        if (this.activeDropdown === menu) {
          this.activeDropdown = null;
        }
      }, 200);
    },

    /* 移动端折叠动画 */
    onEnter(el) {
      el.style.height = "0";
      el.style.opacity = "0";
    },
    afterEnter(el) {
      el.style.height = "auto";
      el.style.opacity = "1";
    },
    onLeave(el) {
      el.style.height = el.scrollHeight + "px";
      el.offsetHeight;
      el.style.height = "0";
      el.style.opacity = "0";
    },
  },
};
</script>

<style scoped>
/* Header */
.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(135deg, var(--header-primary-start), var(--header-primary-end));
  backdrop-filter: blur(10px);
  z-index: 1000;
  transition: all 0.3s ease;
  padding: 15px 0;
}

.header-scrolled {
  background: linear-gradient(135deg, var(--header-primary-start), var(--header-primary-end));
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 10px 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Logo */
.logo {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: transform 0.3s ease;
  z-index: 1001;
}

.logo:hover {
  transform: scale(1.05);
}

.logo-icon {
  height: 50px;
  border-radius: 8px;
}

/* 桌面端导航 */
.desktop-nav {
  display: flex;
  gap: 30px;
  align-items: center;
}

.nav-item-wrapper {
  position: relative;
}

.desktop-nav .nav-link {
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  padding: 5px 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
}

.arrow-down {
  font-size: 10px;
  font-style: normal;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-item-wrapper:hover .arrow-down {
  transform: rotate(180deg);
}

.desktop-nav .nav-link::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #3194b2, #43b4ea);
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 2px;
}

.desktop-nav .nav-link:hover,
.desktop-nav .nav-link.active {
  color: white;
}

.desktop-nav .nav-link:hover::after,
.desktop-nav .nav-link.active::after {
  width: 100%;
}

/* 下拉菜单 */
.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100vw;
  background: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 999;
  margin-left: calc(-50vw + 50%);
  border-top: 3px solid #3194b2;
  overflow: hidden;
}

.dropdown-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 35px 15px;
}

.dropdown-content {
  display: flex;
  gap: 60px;
  justify-content: center;
}

/* 关于华浩菜单 - 横向排列 */
.about-menu {
  gap: 40px;
}

.about-menu .menu-item {
  font-size: 15px;
  font-weight: 500;
}

.menu-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 150px;
}

.menu-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 2px solid #3194b2;
  position: relative;
  opacity: 0;
  transform: translateY(-10px);
  animation: menuTitleFadeIn 0.4s ease forwards;
}

@keyframes menuTitleFadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.menu-title::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #43b4ea, #3194b2);
  transition: width 0.5s ease;
}

.menu-column:hover .menu-title::after {
  width: 100%;
}

.menu-item {
  color: #666;
  text-decoration: none;
  font-size: 14px;
  padding: 8px 12px;
  transition: all 0.3s ease;
  position: relative;
  border-radius: 4px;
  display: block;
  opacity: 0;
  transform: translateY(-10px);
  animation: menuItemFadeIn 0.4s ease forwards;
}

@keyframes menuItemFadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.menu-item:hover {
  color: #3194b2;
  background: rgba(49, 148, 178, 0.08);
  transform: translateX(5px);
}

.menu-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 100%;
  background: linear-gradient(90deg, rgba(49, 148, 178, 0.15), transparent);
  transition: width 0.3s ease;
  border-radius: 4px 0 0 4px;
  z-index: -1;
}

.menu-item:hover::before {
  width: 100%;
}

/* 下拉菜单动画 - 从上往下滑动 */
.dropdown-enter-active {
  animation: slideDown 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.dropdown-leave-active {
  animation: slideUp 0.3s ease-in;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
    max-height: 0;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    max-height: 500px;
  }
}

@keyframes slideUp {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
}

/* 语言切换按钮 */
.language-switcher {
  display: flex;
  gap: 5px;
  margin-left: 10px;
  padding-left: 20px;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
}

.lang-btn {
  min-width: 55px;
  padding: 8px 12px;
  font-size: 13px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.lang-btn.el-button--default {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.85);
}

.lang-btn.el-button--default:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
}

.lang-btn.el-button--primary {
  background: linear-gradient(135deg, #3194b2, #31a7d2);
  border-color: transparent;
}

/* 移动端折叠菜单 */
.navbar-collapse {
  display: none;
  overflow: hidden;
  background: rgba(78, 106, 122, 0.95);
  backdrop-filter: blur(10px);
  margin: 15px -15px -15px;
  transition: height 0.2s ease, opacity 0.2s ease;
}

.navbar-nav {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-item:last-child {
  border-bottom: none;
}

.navbar-collapse .nav-link {
  display: block;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 16px;
  padding: 15px 20px;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
  background: none;
  text-align: left;
  width: 100%;
  border: none;
  cursor: pointer;
}

.navbar-collapse .nav-link:hover,
.navbar-collapse .nav-link:active,
.navbar-collapse .nav-link.router-link-active {
  background: rgba(49, 148, 178, 0.2);
  border-left-color: #3194b2;
  color: white;
}

/* 移动端子菜单 */
.mobile-submenu {
  list-style: none;
  padding: 0;
  margin: 0;
  background: rgba(0, 0, 0, 0.1);
}

.mobile-submenu li {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.mobile-submenu li:last-child {
  border-bottom: none;
}

/* 移动端子菜单标题 */
.mobile-submenu-title {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px 8px 40px;
  margin-top: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-submenu-title:first-child {
  margin-top: 0;
  border-top: none;
}

/* 移动端子菜单链接 - 修复蓝色链接问题 */
.mobile-submenu-link {
  display: block;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  padding: 12px 20px 12px 50px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  position: relative;
}

/* 修复 router-link 的蓝色链接样式 */
.mobile-submenu-link:hover,
.mobile-submenu-link:active,
.mobile-submenu-link.router-link-active {
  background: rgba(49, 148, 178, 0.2);
  color: white;
}

/* 移除 router-link 的默认蓝色链接样式 */
.mobile-submenu-link,
.mobile-submenu-link:visited,
.mobile-submenu-link:link {
  color: rgba(255, 255, 255, 0.8) !important;
}

/* 添加指示箭头 */
.mobile-submenu-link::after {
  content: "→";
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.5;
  font-size: 12px;
  transition: all 0.3s ease;
}

.mobile-submenu-link:hover::after {
  opacity: 1;
  right: 15px;
}

/* 移动端语言切换 */
.language-mobile {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 15px 20px !important;
  border-bottom: none !important;
  background: rgba(0, 0, 0, 0.1);
}

.language-switcher-mobile {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.language-switcher-mobile .lang-btn {
  flex: 1;
  max-width: 100px;
}

/* 汉堡按钮 */
.navbar-toggler {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 30px;
  background: transparent;
  border: 0;
  border-radius: 4px;
  padding: 5px 4px;
  cursor: pointer;
  z-index: 1001;
  transition: all 0.3s ease;
}

.navbar-toggler-icon {
  width: 100%;
  height: 2px;
  background: white;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.navbar-toggler.active .navbar-toggler-icon:nth-child(1) {
  transform: rotate(45deg) translate(5px, 2px);
}

.navbar-toggler.active .navbar-toggler-icon:nth-child(2) {
  opacity: 0;
  transform: translateX(0px);
}

.navbar-toggler.active .navbar-toggler-icon:nth-child(3) {
  transform: rotate(-45deg) translate(5px, -2px);
}

/* 折叠动画 */
.collapse-enter-active,
.collapse-leave-active {
  transition: height 0.2s ease, opacity 0.2s ease;
}

.collapse-enter-from,
.collapse-leave-to {
  height: 0 !important;
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .desktop-nav {
    display: none;
  }

  .navbar-toggler {
    display: flex;
  }

  .navbar-collapse {
    display: block;
  }

  .logo-icon {
    height: 40px;
  }

  .header {
    padding: 12px 0;
  }

  .header-scrolled {
    padding: 10px 0;
  }

  .dropdown-menu {
    display: none !important;
  }
}

@media (max-width: 480px) {
  .logo-icon {
    height: 35px;
  }

  .navbar-toggler {
    width: 28px;
    height: 22px;
  }

  .navbar-collapse .nav-link {
    font-size: 15px;
    padding: 12px 18px;
  }

  .mobile-submenu-link {
    padding: 10px 18px 10px 40px;
    font-size: 13px;
  }

  .mobile-submenu-title {
    padding: 8px 18px 6px 40px;
    font-size: 13px;
  }
}
</style>
